/**
 * Material invoices panel (Aug 21, 2026) — the COGS ingestion surface on
 * /financials. Upload a RoofHub/SRS PDF → best-effort parse → ALWAYS a human
 * review form → save. Also: manual entry, mark paid/unpaid, re-match to a
 * deal, view stored PDF. 1,428 rows were backfilled from the Aug 21 RoofHub
 * register export (source='roofhub_backfill').
 *
 * Sep 14, 2026: + the SRS payment-history CSV import (MaterialPaymentsImport)
 * — payments land in material_invoice_payments and a trigger derives each
 * invoice's paid state; the row shows the SRS payment date + confirmation #,
 * and a partial payment shows what's applied vs owed.
 *
 * Finance-tier RLS only (admin / sales_director / finance + supers).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { fmtUSD } from "../../lib/analytics";
import type { MaterialInvoice, MaterialInvoiceLine } from "../../lib/types";
import {
  extractPdfText, parseSrsInvoice, computeSquaresFromLines,
} from "../../lib/materialInvoiceParse";
import { num, invoiceOwed } from "../../lib/financialsData";
import MaterialPaymentsImport from "./MaterialPaymentsImport";

interface DealLite {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  stage: string | null;
  build_completed_date: string | null;
}

interface DraftInvoice {
  invoice_number: string;
  order_number: string;
  invoice_date: string;
  due_date: string;
  job_name: string;
  job_address: string;
  total: string;
  line_items: MaterialInvoiceLine[];
  computed_squares: string;
  notes: string;
  pdf_storage_path: string | null;
  fileName?: string;
  fields_found?: number;
}

const EMPTY_DRAFT: DraftInvoice = {
  invoice_number: "", order_number: "", invoice_date: "", due_date: "",
  job_name: "", job_address: "", total: "", line_items: [],
  computed_squares: "", notes: "", pdf_storage_path: null,
};

export default function MaterialInvoicesPanel({
  invoices, onChanged,
}: {
  invoices: MaterialInvoice[];
  onChanged: () => void;
}) {
  const { member } = useAuth();
  const [filter, setFilter] = useState<"all" | "unpaid" | "unmatched">("unpaid");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<DraftInvoice | null>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [matchDeal, setMatchDeal] = useState<DealLite | null>(null);
  const [candidates, setCandidates] = useState<DealLite[]>([]);
  const [dealSearch, setDealSearch] = useState("");
  const [rematchId, setRematchId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => {
    let r = [...invoices].sort((a, z) => (z.invoice_date ?? "").localeCompare(a.invoice_date ?? ""));
    if (filter === "unpaid") r = r.filter((i) => i.paid_status === "unpaid");
    if (filter === "unmatched") r = r.filter((i) => !i.deal_id);
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((i) =>
        (i.invoice_number ?? "").toLowerCase().includes(q) ||
        (i.job_name ?? "").toLowerCase().includes(q) ||
        (i.job_address ?? "").toLowerCase().includes(q) ||
        (i.paid_reference ?? "").toLowerCase().includes(q));
    }
    return r.slice(0, 60);
  }, [invoices, filter, search]);

  // "owed" nets out partial payments recorded by the CSV import
  const unpaidTotal = useMemo(
    () => invoices.reduce((s, i) => s + invoiceOwed(i), 0),
    [invoices]);
  const todayISO = new Date().toISOString().slice(0, 10);
  const overdueTotal = useMemo(
    () => invoices.filter((i) => i.paid_status === "unpaid" && (i.due_date ?? "9999") < todayISO)
      .reduce((s, i) => s + invoiceOwed(i), 0),
    [invoices, todayISO]);
  const unmatchedCount = useMemo(() => invoices.filter((i) => !i.deal_id).length, [invoices]);

  // ── deal candidate search (address-derived + free text) ──
  useEffect(() => {
    let cancelled = false;
    const q = (dealSearch || draft?.job_address || "").trim();
    if (!draft && !rematchId) { setCandidates([]); return; }
    if (q.length < 3) { setCandidates([]); return; }
    const t = setTimeout(async () => {
      // street-number + first word when it looks like an address, else name search
      const m = q.match(/^\s*(\d{1,6})\s+([A-Za-z]+)/);
      let query = supabase
        .from("deals")
        .select("id,homeowner_name,property_street,property_city,stage,build_completed_date")
        .limit(6);
      if (m) {
        query = query.ilike("property_street", `${m[1]} ${m[2]}%`);
      } else {
        // Strip ilike wildcards AND PostgREST .or() grammar chars — a comma or
        // paren in "Smith, John (rental)" would split/close the or-expression
        // and 400 the whole query (audit finding).
        const safe = q.replace(/[%_\\,()]/g, " ").replace(/\s+/g, " ").trim();
        if (safe.length < 3) { setCandidates([]); return; }
        query = query.or(`homeowner_name.ilike.%${safe}%,property_street.ilike.%${safe}%`);
      }
      const { data } = await query;
      if (!cancelled) setCandidates((data ?? []) as DealLite[]);
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dealSearch, draft, rematchId]);

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setErr(null);
    setParsing(true);
    setMatchDeal(null);
    setDealSearch("");
    try {
      // 1. store the PDF (audit trail even when parsing fails)
      const path = `material-invoices/${Date.now()}-${file.name.replace(/[^A-Za-z0-9._-]/g, "_")}`;
      const up = await supabase.storage.from("deal-documents")
        .upload(path, file, { contentType: file.type || "application/pdf" });
      const storedPath = up.error ? null : path;
      // 2. extract + parse (best-effort; blank form on failure)
      const text = await extractPdfText(file);
      const parsed = parseSrsInvoice(text);
      const sq = computeSquaresFromLines(parsed.line_items);
      setDraft({
        invoice_number: parsed.invoice_number ?? "",
        order_number: parsed.order_number ?? "",
        invoice_date: parsed.invoice_date ?? "",
        due_date: parsed.due_date ?? "",
        job_name: parsed.job_name ?? "",
        job_address: parsed.job_address ?? "",
        total: parsed.total !== null ? String(parsed.total) : "",
        line_items: parsed.line_items,
        computed_squares: sq !== null ? String(sq) : "",
        notes: "",
        pdf_storage_path: storedPath,
        fileName: file.name,
        fields_found: parsed.fields_found,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't read that PDF — use manual entry below.");
      setDraft({ ...EMPTY_DRAFT, fileName: file.name });
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function saveDraft() {
    if (!draft) return;
    if (!draft.invoice_number.trim()) { setErr("Invoice number is required (it's the duplicate guard)."); return; }
    setSaving(true);
    setErr(null);
    const { error } = await supabase.from("material_invoices").insert({
      supplier: "SRS",
      invoice_number: draft.invoice_number.trim(),
      order_number: draft.order_number.trim() || null,
      invoice_date: draft.invoice_date || null,
      due_date: draft.due_date || null,
      job_name: draft.job_name.trim() || null,
      job_address: draft.job_address.trim() || null,
      total: num(draft.total),
      line_items: draft.line_items,
      computed_squares: draft.computed_squares ? num(draft.computed_squares) : null,
      deal_id: matchDeal?.id ?? null,
      match_status: matchDeal ? "confirmed" : "unmatched",
      matched_by: matchDeal ? member?.id ?? null : null,
      pdf_storage_path: draft.pdf_storage_path,
      source: draft.fileName ? "upload" : "manual",
      notes: draft.notes.trim() || null,
      created_by: member?.id ?? null,
    });
    setSaving(false);
    if (error) {
      setErr(error.code === "23505"
        ? `Invoice ${draft.invoice_number} is already imported — nothing saved.`
        : `Save failed: ${error.message}`);
      return;
    }
    setDraft(null);
    setMatchDeal(null);
    onChanged();
  }

  async function togglePaid(inv: MaterialInvoice) {
    const paid = inv.paid_status === "unpaid";
    const { error } = await supabase.from("material_invoices").update({
      paid_status: paid ? "paid" : "unpaid",
      paid_at: paid ? new Date().toISOString() : null,
      paid_by: paid ? member?.id ?? null : null,
      // provenance — a CSV-import undo never touches a manually-toggled row
      paid_source: paid ? "manual" : null,
    }).eq("id", inv.id);
    if (error) { setErr(`Update failed: ${error.message}`); return; }
    // Audit-log every financial mark-paid (Jul 15 rule) — best-effort.
    void supabase.from("finance_audit_log").insert({
      deal_id: inv.deal_id,
      actor_id: member?.id ?? null,
      action: paid ? "material_invoice_marked_paid" : "material_invoice_marked_unpaid",
      is_financial: true,
      after_jsonb: { invoice_number: inv.invoice_number, supplier: inv.supplier, total: num(inv.total), source: "financials_material_panel" },
    }).then(({ error: e }) => { if (e) console.warn("material invoice audit log failed", e); });
    onChanged();
  }

  async function applyRematch(inv: MaterialInvoice, deal: DealLite | null) {
    const { error } = await supabase.from("material_invoices").update({
      deal_id: deal?.id ?? null,
      match_status: deal ? "confirmed" : "unmatched",
      matched_by: deal ? member?.id ?? null : null,
    }).eq("id", inv.id);
    if (error) { setErr(`Match failed: ${error.message}`); return; }
    void supabase.from("finance_audit_log").insert({
      deal_id: deal?.id ?? inv.deal_id,
      actor_id: member?.id ?? null,
      action: "material_invoice_rematched",
      is_financial: false,
      after_jsonb: {
        invoice_number: inv.invoice_number, total: num(inv.total),
        from_deal_id: inv.deal_id, to_deal_id: deal?.id ?? null,
        source: "financials_material_panel",
      },
    }).then(({ error: e }) => { if (e) console.warn("material invoice audit log failed", e); });
    setRematchId(null); setDealSearch(""); onChanged();
  }

  async function viewPdf(inv: MaterialInvoice) {
    if (!inv.pdf_storage_path) return;
    const w = window.open("about:blank", "_blank");
    const { data } = await supabase.storage.from("deal-documents")
      .createSignedUrl(inv.pdf_storage_path, 3600);
    if (data?.signedUrl && w) w.location.href = data.signedUrl;
    else w?.close();
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="flex items-center justify-between px-4 pt-4 flex-wrap gap-2">
        <h2 className="text-[15px] font-extrabold">🧾 Material invoices — SRS / RoofHub</h2>
        <div className="flex gap-2 text-[11px] font-bold flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
            {fmtUSD(unpaidTotal)} unpaid
          </span>
          {overdueTotal > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300">
              {fmtUSD(overdueTotal)} overdue
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {unmatchedCount} need a job match
          </span>
        </div>
      </div>

      {/* upload + manual entry */}
      <div className="px-4 pt-3 flex gap-2 flex-wrap items-center">
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
          onChange={(e) => handleFiles(e.target.files)} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={parsing}
          className="px-3 py-2 rounded-lg bg-orange-600 text-white text-[12.5px] font-bold hover:bg-orange-700 disabled:opacity-50">
          {parsing ? "Reading PDF…" : "📄 Upload invoice PDF"}
        </button>
        <button
          onClick={() => { setDraft({ ...EMPTY_DRAFT }); setMatchDeal(null); setErr(null); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-[12.5px] font-bold hover:bg-slate-50">
          ✎ Manual entry
        </button>
        <div className="flex gap-1 ml-auto">
          {(["unpaid", "unmatched", "all"] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1.5 rounded-full text-[11.5px] font-bold border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>
              {f === "all" ? "All" : f === "unpaid" ? "Unpaid" : "Unmatched"}
            </button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice # / name / address"
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[12.5px] w-56" />
      </div>

      {/* SRS payment-history CSV → matched payments (Sep 14, 2026) */}
      <MaterialPaymentsImport onChanged={onChanged} />

      {err && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12.5px]">
          {err}
        </div>
      )}

      {/* review / entry form */}
      {draft && (
        <div className="mx-4 mt-3 border border-orange-200 bg-orange-50/40 rounded-xl p-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-[13px] font-extrabold">
              {draft.fileName ? `Review parsed invoice — ${draft.fileName}` : "New invoice — manual entry"}
            </div>
            {draft.fileName && (
              <span className="text-[11px] text-slate-500">
                parser found {draft.fields_found ?? 0}/4 headline fields — check everything before saving
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12.5px]">
            {([
              ["invoice_number", "Invoice # *"],
              ["order_number", "Order #"],
              ["invoice_date", "Invoice date"],
              ["due_date", "Due date"],
              ["job_name", "Job / PO name"],
              ["job_address", "Job address"],
              ["total", "Total $"],
              ["computed_squares", "Squares (bundles ÷ 3)"],
            ] as Array<[keyof DraftInvoice, string]>).map(([k, label]) => (
              <label key={k} className="flex flex-col gap-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
                <input
                  type={k === "invoice_date" || k === "due_date" ? "date" : "text"}
                  value={String(draft[k] ?? "")}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  className="border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
              </label>
            ))}
          </div>
          {draft.line_items.length > 0 && (
            <div className="mt-2 text-[11.5px] text-slate-600">
              {draft.line_items.length} line items parsed (first: “{draft.line_items[0].description.slice(0, 48)}”)
            </div>
          )}
          {/* deal match */}
          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Match to job</div>
            {matchDeal ? (
              <div className="flex items-center gap-2 text-[12.5px] bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                <span className="font-bold">✓ {matchDeal.homeowner_name}</span>
                <span className="text-slate-500">{matchDeal.property_street}, {matchDeal.property_city}</span>
                <button onClick={() => setMatchDeal(null)} className="ml-auto text-sky-700 font-bold">change</button>
              </div>
            ) : (
              <>
                <input value={dealSearch} onChange={(e) => setDealSearch(e.target.value)}
                  placeholder="Search by address or homeowner (defaults to the parsed address)"
                  className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[12.5px] w-full bg-white" />
                {candidates.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                    {candidates.map((c) => (
                      <button key={c.id} onClick={() => setMatchDeal(c)}
                        className="w-full text-left px-2.5 py-1.5 text-[12.5px] hover:bg-orange-50">
                        <b>{c.homeowner_name}</b>
                        <span className="text-slate-500"> — {c.property_street}, {c.property_city} · {c.stage}
                          {c.build_completed_date ? ` · built ${c.build_completed_date}` : ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => void saveDraft()} disabled={saving}
              className="px-3.5 py-2 rounded-lg bg-orange-600 text-white text-[12.5px] font-bold hover:bg-orange-700 disabled:opacity-50">
              {saving ? "Saving…" : "Confirm & save"}
            </button>
            <button onClick={() => { setDraft(null); setMatchDeal(null); }}
              className="px-3.5 py-2 rounded-lg border border-slate-300 text-slate-600 text-[12.5px] font-bold">
              Cancel
            </button>
            {!matchDeal && (
              <span className="text-[11px] text-slate-500 self-center">
                Saving without a match is fine — it still counts in company COGS and lands in the "Unmatched" queue.
              </span>
            )}
          </div>
        </div>
      )}

      {/* list */}
      <div className="overflow-x-auto px-4 py-3">
        <table className="w-full text-[12.5px] min-w-[860px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b-2 border-slate-200">
              <th className="py-2 pr-2">Invoice</th>
              <th className="pr-2">Job</th>
              <th className="pr-2 text-right">Total</th>
              <th className="pr-2">Due</th>
              <th className="pr-2">Status</th>
              <th className="pr-2">Match</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => {
              const overdue = inv.paid_status === "unpaid" && (inv.due_date ?? "9999") < todayISO;
              const cm = num(inv.total) < 0;
              return (
                <tr key={inv.id} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-2 whitespace-nowrap">
                    <div className="font-bold">{inv.invoice_number}</div>
                    <div className="text-slate-500 text-[11px]">{inv.invoice_date ?? "—"}{cm ? " · credit memo" : ""}</div>
                  </td>
                  <td className="pr-2">
                    <div className="font-semibold">{inv.job_name ?? "—"}</div>
                    <div className="text-slate-500 text-[11px] max-w-[280px] truncate">{inv.job_address ?? ""}</div>
                  </td>
                  <td className={`pr-2 text-right font-bold tabular-nums ${cm ? "text-emerald-700" : ""}`}>{fmtUSD(num(inv.total))}</td>
                  <td className="pr-2 whitespace-nowrap">{inv.due_date ?? "—"}</td>
                  <td className="pr-2 whitespace-nowrap">
                    {inv.paid_status === "paid" ? (
                      <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">paid</span>
                    ) : overdue ? (
                      <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-rose-100 text-rose-800 border border-rose-300">overdue</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-amber-50 text-amber-700 border border-amber-200">unpaid</span>
                    )}
                    {inv.paid_status === "paid" && (inv.paid_at || inv.paid_reference) && (
                      <div className="text-[10.5px] text-slate-500 mt-0.5" title={inv.paid_source === "payment_import" ? "From the SRS payment-history import" : inv.paid_source === "manual" ? "Marked paid in this panel" : undefined}>
                        {inv.paid_at ? `${inv.paid_at.slice(5, 7)}/${inv.paid_at.slice(8, 10)}/${inv.paid_at.slice(0, 4)}` : ""}
                        {inv.paid_reference ? `${inv.paid_at ? " · " : ""}#${inv.paid_reference}` : ""}
                      </div>
                    )}
                    {inv.paid_status === "unpaid" && num(inv.paid_amount) !== 0 && (
                      <div className="text-[10.5px] text-amber-700 font-bold mt-0.5">
                        {fmtUSD(num(inv.paid_amount))} applied · {fmtUSD(invoiceOwed(inv))} owed
                      </div>
                    )}
                  </td>
                  <td className="pr-2">
                    {rematchId === inv.id ? (
                      <div className="min-w-[240px]">
                        <input autoFocus value={dealSearch} onChange={(e) => setDealSearch(e.target.value)}
                          placeholder="Address or homeowner…"
                          className="border border-slate-300 rounded-lg px-2 py-1 text-[12px] w-full" />
                        {candidates.map((c) => (
                          <button key={c.id} onClick={() => void applyRematch(inv, c)}
                            className="block w-full text-left px-2 py-1 text-[11.5px] hover:bg-orange-50 border-b border-slate-100">
                            <b>{c.homeowner_name}</b> <span className="text-slate-500">{c.property_street}</span>
                          </button>
                        ))}
                        <div className="flex gap-2 mt-1">
                          <button onClick={() => void applyRematch(inv, null)} className="text-[11px] text-slate-500 font-bold">clear match</button>
                          <button onClick={() => { setRematchId(null); setDealSearch(""); }} className="text-[11px] text-slate-500">close</button>
                        </div>
                      </div>
                    ) : inv.deal_id ? (
                      <button onClick={() => { setRematchId(inv.id); setDealSearch(""); }}
                        className="text-[11.5px] text-emerald-700 font-bold hover:underline"
                        title={inv.match_status === "confirmed" ? "Confirmed by a person" : "Auto-matched — click to change"}>
                        {inv.match_status === "confirmed" ? "✓ matched" : "≈ auto-matched"}
                      </button>
                    ) : (
                      <button onClick={() => { setRematchId(inv.id); setDealSearch(inv.job_address ?? ""); }}
                        className="text-[11.5px] text-amber-700 font-bold hover:underline">match →</button>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    <button onClick={() => void togglePaid(inv)}
                      className="text-[11.5px] font-bold text-sky-700 hover:underline mr-2">
                      {inv.paid_status === "paid" ? "mark unpaid" : "mark paid"}
                    </button>
                    {inv.pdf_storage_path && (
                      <button onClick={() => void viewPdf(inv)} className="text-[11.5px] font-bold text-slate-500 hover:underline">PDF</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-slate-400">No invoices match this filter.</td></tr>
            )}
          </tbody>
        </table>
        {rows.length === 60 && (
          <div className="text-[11px] text-slate-400 pt-1">Showing the 60 most recent — narrow with search.</div>
        )}
      </div>
    </div>
  );
}
